import { useRef, useEffect } from 'react'
import { css } from '@emotion/react'
import uniq from 'lodash/uniq'
import { Button, Tooltip, Tab, mq } from 'plume-ui'
import { CompanyTabType } from '../../../../../types'
import { CompanyData } from '../../../../../types/api'
import { useUser } from '../../../../../contexts/AuthContext'
import { isEnterpriseWithTrial } from '@/lib/user'
import { companyTabLabel, isPrivate } from '@/lib/company'
import { Navbar } from '../../../../common/Navbar'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'

const nonEnterpriseTabList: CompanyTabType[] = [
  'basic-info',
  'news',
  'shareholders',
  'directors',
  'performances',
  'partnerships',
]

const privateTabList: CompanyTabType[] = [
  'basic-info',
  'finance-info',
  'news',
  'shareholders',
  'directors',
  'performances',
  'employees',
  'partnerships',
  'benchmarks',
  'others',
]

const ipoTabList: CompanyTabType[] = [
  'basic-info',
  'finance-info',
  'ipo-data',
  'news',
  'directors',
  'performances',
  'employees',
  'partnerships',
  'benchmarks',
  'others',
]

export const companyTabList: CompanyTabType[] = uniq([
  ...privateTabList,
  ...ipoTabList,
])

type Props = {
  data: CompanyData
  activeTab: CompanyTabType | null
  showDownloadModal: () => void
  showTrialModal: () => void
}

export const CompanyTabGroup: React.FC<Props> = (props) => {
  const user = useUser()
  const ref = useRef<HTMLDivElement>(null)
  const tabList = !isEnterpriseWithTrial(user)
    ? nonEnterpriseTabList
    : isPrivate(props.data)
      ? privateTabList
      : ipoTabList

  useEffect(() => {
    const activeTab = ref.current?.querySelector<HTMLElement>('.is-active')
    if (activeTab === null || activeTab === undefined) return
    // ゲスト向けモバイルのタブバーのスクロール。Tab.Groupにrefをつけられないので、直下の子供を指定している。
    ref.current?.children[0].scroll({
      left: activeTab.offsetLeft - 32,
      behavior: 'smooth',
    })
  }, [props.activeTab])

  return (
    <Navbar
      data-test-id="tab-group"
      css={css`
        padding: 8px 0px;
        height: 48px;
        ${mq.large} {
          height: auto;
          padding: 11px 0;
          position: sticky;
          top: 160px;
          z-index: 19;
        }
      `}
      isUnappliedSticky={isEnterpriseWithTrial(user)}
    >
      <div ref={ref}>
        <Tab.Group
          css={css`
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            white-space: nowrap;
            padding: 0px 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              white-space: nowrap;
              gap: 4px;
            `}
          >
            {tabList.map((tab, i) => (
              <CompanyNavigationTab
                key={i}
                tab={tab}
                data={props.data}
                activeTab={props.activeTab}
              />
            ))}
          </div>
          <div>
            {isEnterpriseWithTrial(user) ? (
              <DownloadButton onClick={props.showDownloadModal} />
            ) : (
              <Tooltip
                className="follow-button-tooltip"
                label={'有料契約限定の機能です'}
                place="bottom"
              >
                <DownloadButton onClick={props.showTrialModal} />
              </Tooltip>
            )}
          </div>
        </Tab.Group>
      </div>
    </Navbar>
  )
}

const CompanyNavigationTab: React.FC<{
  tab: CompanyTabType
  activeTab: CompanyTabType | null
  data: CompanyData
}> = (props) => {
  const href = `/companies/${props.data.shortname}#${props.tab}`
  if (props.tab === 'employees' && props.data.employeesShift?.length === 0) {
    return null
  }

  return (
    <Tab
      active={props.activeTab === props.tab}
      as="a"
      href={href}
      css={css`
        ${mq.untilMedium} {
          font-size: 14px;
          height: 32px;
        }
      `}
    >
      {companyTabLabel[props.tab]}
    </Tab>
  )
}

const DownloadButton: React.FC<{ onClick: () => void }> = (props) => {
  return (
    <Button
      className="download-button"
      color="black"
      outlined
      size="small"
      css={css`
        padding-left: 12px !important;
        white-space: nowrap;
      `}
      onClick={props.onClick}
    >
      <IcomoonIcon
        name="download"
        css={css`
          margin-right: 7px;
        `}
      />
      ダウンロード
    </Button>
  )
}
